import React from 'react';
import { Alert, Box, Paper, Stack, Typography } from '@mui/material';
import { AccountBalanceWallet, Groups, ShowChart } from '@mui/icons-material';

/** Explains capture (partners) vs deploy (stock plans allocate only). */
const PartnerCapitalFlowGuide: React.FC<{ variant?: 'page' | 'compact' }> = ({ variant = 'page' }) => {
  const steps = [
    {
      icon: <Groups color="primary" />,
      title: '1. Register partner',
      body: 'Add the partner name, contact, and optional portal access on this screen.',
    },
    {
      icon: <AccountBalanceWallet color="success" />,
      title: '2. Capture capital',
      body: 'Record each contribution (amount, date, reference). This increases “Available to allocate”.',
    },
    {
      icon: <ShowChart color="warning" />,
      title: '3. Purchase on a plan',
      body: 'Open Stock Picks → Execution → record purchase and split the total cost across partners. That sets each partner’s deployment automatically.',
    },
  ];

  if (variant === 'compact') {
    return (
      <Alert severity="info" variant="outlined" sx={{ py: 0.5 }}>
        <strong>Flow:</strong> Capture contributions here → purchase on{' '}
        <strong>Stock Picks → Execution</strong> and split cost across partners.
      </Alert>
    );
  }

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: 'action.hover' }}>
      <Typography variant="subtitle2" color="primary" gutterBottom>
        Capital flow
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Partner money is captured once on this screen, then deployed per stock plan by allocation only.
      </Typography>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
        {steps.map((step) => (
          <Box key={step.title} sx={{ flex: 1, display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
            <Box sx={{ mt: 0.25 }}>{step.icon}</Box>
            <Box>
              <Typography variant="subtitle2">{step.title}</Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {step.body}
              </Typography>
            </Box>
          </Box>
        ))}
      </Stack>
    </Paper>
  );
};

export default PartnerCapitalFlowGuide;
